import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  getInitFormData,
  applySlip,
  CalAm,
  CalAmBulk,
} from "@shared/utils/CommonUtils";
import { fnDrilldown } from "@shared/utils/DrilldownUtils";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnForm, { ValueChangedEvent } from "@widgets/SnForm";
import SnResizeBar from "@shared/components/SnResizeBar";
import { showPopupModal } from "@widgets/CommonModal";
import SnButton from "@shared/components/SnButton";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SN_TRA_IBL_REG_002_POP_001 from "./SN_TRA_IBL_REG_002_POP_001";
import SN_TRA_IBL_REG_002_POP_002 from "./SN_TRA_IBL_REG_002_POP_002";
import { dateToString } from "@shared/utils/DateUtils";

interface Props {
  Menuprops: menuInfo;
}

// 통관등록
const SN_TRA_IBL_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
  });

  const [columns, setColums] = useState<any>({ grid1: [] });
  const [dataSources, setDataSources] = useState<any>({ grid1: [] });
  const [formControls, setFormControls] = useState<any>({ freeform1: [] });
  const [formDatas, setFormDatas] = useState<any>({ freeform1: null });
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setColums({ grid1: rtn.grid.grid2 });
        setFormControls({ freeform1: rtn.form.freeform1 });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = formRefs.current["freeform1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid1"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
        return;
      }
    }
    try {
      await search_popup();
    } catch (error) {}
  };

  const getPopupInitialData = () => {
    const formData =
      formRefs.current["freeform1"].current?.getFormdata?.() ?? {};
    return {
      cd_cust: formData.cd_cust || "",
      nm_cust: formData.nm_cust || "",
      nm_cust_sort: formData.nm_cust || "",
    };
  };

  const search_popup = async () => {
    const component = (
      <SN_TRA_IBL_REG_002_POP_001 selectedData={getPopupInitialData()} />
    );
    const result = await showPopupModal({
      title: "통관도움",
      children: component,
    });
    if (!result || result === "close") return;
    const headerResult = result[0];
    const params = {
      id: "sn_tra_ibl_reg_002_01",
      params: { no_ip: headerResult[0].no_ip },
    };
    const data: any = await fnGetDataSet(params);
    setFormDatas({ freeform1: data[0]?.[0] ?? null });
    setDataSources({ grid1: data[1] || [] });
    setSelectedDocu(data[0]?.[0]?.no_ip ?? "");

    formRefs.current["freeform1"].current.resetUpdate();
    gridRefs.current["grid1"].current.resetUpdate();
  };

  const onInsert = async () => {
    try {
      if (formRefs.current["freeform1"].current.getChange().insert.length > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정중인 건이 있습니다. 저장하지 않고 계속 하시겠습니까?"
        );
        if (!confirm) return;
      }

      formRefs.current["freeform1"].current.resetUpdate();
      gridRefs.current["grid1"].current?.deleteAllRows();
      gridRefs.current["grid1"].current.resetUpdate();
      setSelectedDocu("");

      const today = dateToString(new Date());
      formRefs.current["freeform1"].current.addRow({
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        nm_biz: userInfo.nm_biz,
        cd_dept: userInfo.cd_dept,
        nm_dept: userInfo.nm_dept,
        cd_exch: "000",
        cd_gp: userInfo.cd_gp,
        nm_gp: userInfo.nm_gp,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        dt_custom: today,
        rt_exch: 1,
        am_custom: 0,
        am: 0,
        am_k: 0,
        cd_cust: "",
        fg_trns: "",
        no_ip: "",
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onDelete = async () => {
    if (!formRefs.current["freeform1"].current.checkDataExist()) return;

    const allRows: any[] = [];
    gridRefs.current["grid1"].current.forEachNode((node: any) =>
      allRows.push(node.data)
    );
    if (allRows.some((r) => Number(r.qt_rcv) > 0)) {
      await showDialog("alter", "입고 처리된 수량이 존재합니다.");
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid1"].current?.deleteAllRows();
    formRefs.current["freeform1"].current?.deleteForm();
    setSelectedDocu("");

    const realDeletedRowsGrid =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    const realDeletedRowsForm =
      formRefs.current["freeform1"].current.getChange().delete ?? [];
    if (
      Array.isArray(realDeletedRowsGrid) &&
      realDeletedRowsGrid.length < 1 &&
      Array.isArray(realDeletedRowsForm) &&
      realDeletedRowsForm.length < 1
    ) {
      gridRefs.current["grid1"].current.resetUpdate();
      formRefs.current["freeform1"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      return gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {
      return null;
    }
  };

  const onBeforeAddRow = () => {
    try {
      const maxNoLine =
        gridRefs.current["grid1"].current.getMaxValue("no_line");
      return {
        cd_firm: userInfo.cd_firm,
        no_ip: formRefs.current["freeform1"].current.getFormdata()?.no_ip ?? "",
        no_line_req: 0,
        no_line_po: 0,
        no_line_rcv_ref: 0,
        no_line_lc: 0,
        no_line: (maxNoLine || 0) + 1,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDeleteRow = async () => {
    const grid = gridRefs.current["grid1"].current;
    const selectedRows = grid?.getSelectedRows() ?? [];
    if (selectedRows.length === 0) return;

    if (selectedRows.length >= grid.getRowCount()) {
      await showDialog(
        "alter",
        "상세내역만 전체 삭제할 수 없습니다.\n상단도 함께 삭제하세요."
      );
      return;
    }

    if (selectedRows.some((r: any) => Number(r.qt_rcv) > 0)) {
      await showDialog("alter", "입고 처리된 수량이 존재합니다.");
      return;
    }
    grid?.deleteSelectedRows();
    onAfterSetFooter();
  };

  const onAfterSetFooter = () => {
    const footerData = gridRefs.current["grid1"].current.getFooterData();
    formRefs.current["freeform1"].current.setDataValue(
      "am",
      footerData.am,
      "noEvent"
    );
    formRefs.current["freeform1"].current.setDataValue(
      "am_k",
      footerData.am_k,
      "noEvent"
    );
  };

  const onContextMenu = async (params: any) => {
    const targetMenu = params?.menu;
    if (!targetMenu) return;

    const noIp = formRefs.current["freeform1"].current?.getFormdata?.()?.no_ip;
    if (
      !noIp ||
      formRefs.current["freeform1"].current.getChange().insert.length > 0
    ) {
      await showDialog(
        "alter",
        "수정중인 내용이 있습니다. 저장 후 처리하세요."
      );
      return;
    }

    await fnDrilldown({
      cd_menu: targetMenu,
      no_docu: noIp,
      no_line: "",
      fg_map: "003",
    });
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();

      if (!formRefs.current["freeform1"].current.requiredValuesCheck())
        return false;
      if (!gridRefs.current["grid1"].current.requiredValuesCheck())
        return false;

      const traIphChanges = formRefs.current["freeform1"].current.getChange();
      const traIplChanges = gridRefs.current["grid1"].current.getChange();
      if (
        traIphChanges.modifiedRowcount === 0 &&
        traIplChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const headerInserts = traIphChanges.insert ?? [];
      const lineInserts = traIplChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (headerInserts.length > 0 && !headerInserts[0].no_ip) {
        const noSlip = await applySlip({
          cd_column: "no_ip",
          cd_module: Menuprops.cd_module,
          no_line: 7,
          cd_prefix: "IPL",
          dt: headerInserts[0].dt_custom,
          girdrefs: [gridRefs.current["grid1"]],
          formrefs: [formRefs.current["freeform1"]],
          inserts: [traIphChanges, traIplChanges],
          cd_menu: Menuprops.cd_menu,
        });
        if (!noSlip) return false;
      }

      const saveData = {
        tra_iph: traIphChanges,
        tra_ipl: traIplChanges,
      };
      const ok = await fnSave("SN_TRA_IBL_REG_002_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid1"].current.resetUpdate(true);
        formRefs.current["freeform1"].current.resetUpdate(true);
        return false;
      }

      formRefs.current["freeform1"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();
      setSelectedDocu(
        formRefs.current["freeform1"].current.getFormdata()?.no_ip ?? ""
      );
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      formRefs.current["freeform1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGridBtnsClick = async (id: string) => {
    if (id === "btnApplyBl") {
      if (
        formRefs.current["freeform1"].current.getChange().insert.length === 0
      ) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      // B/L적용 조회는 "global"이 아니라 통관 화면 헤더의 사업장(cd_biz)·거래처(cd_cust)로 한다.
      const custBiz =
        formRefs.current["freeform1"].current.getFormdata()?.cd_biz ||
        userInfo.cd_biz;
      const component = (
        <SN_TRA_IBL_REG_002_POP_002
          selectedData={{ cd_biz: custBiz, ...getPopupInitialData() }}
        />
      );
      const result = await showPopupModal({
        title: "B/L적용",
        children: component,
        width: "80%",
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult: any[] = result[1];
      if (!headerResult[0]) return;

      for (const key in headerResult[0]) {
        formRefs.current["freeform1"].current.setDataValue(
          key,
          headerResult[0][key],
          "noEvent"
        );
      }
      if (lineResult[0]) {
        formRefs.current["freeform1"].current.setDataValue(
          "no_bl",
          lineResult[0].no_req,
          "noEvent"
        );
        formRefs.current["freeform1"].current.setDataValue(
          "cd_gp",
          lineResult[0].cd_gp,
          "noEvent"
        );
      }

      lineResult.forEach((row: any) => {
        const rowNode = onAddRow();
        if (!rowNode) return;
        for (const key in row) {
          if (key === "no_line") continue;
          rowNode.setDataValue(key, row[key], "noEvent");
        }
        rowNode.setDataValue("no_line_req", row.no_line, "noEvent");
      });
      await CalAmBulk(
        formRefs.current["freeform1"],
        gridRefs.current["grid1"],
        "rt_exch"
      );
      calcTariffAll();
    }
  };

  const onValueChanged = async (event: ValueChangedEvent) => {
    if (!event) return;

    if (event.colId === "no_ip") {
      gridRefs.current["grid1"].current?.forEachNode((node: any) => {
        node.setDataValue("no_ip", event.newValue, "noEvent");
      });
      return;
    }

    await CalAmBulk(
      formRefs.current["freeform1"],
      gridRefs.current["grid1"],
      event.colId
    );
    if (event.colId === "rt_exch") calcTariffAll();
  };

  const onCellValueChangedL = async (event: CellValueChangedEvent) => {
    if (!event) return;

    await CalAm(
      formRefs.current["freeform1"],
      gridRefs.current["grid1"],
      event.colId
    );
    calcTariff(event.rowNode);
  };

  // 관세액 = 원화금액(am_k) x 관세율(rt_tariff) / 100
  const calcTariff = (node: any) => {
    const d = node?.data;
    if (!d) return;
    const amTariff = Math.round(
      ((Number(d.am_k) || 0) * (Number(d.rt_tariff) || 0)) / 100
    );
    if (d.am_tariff !== amTariff)
      node.setDataValue("am_tariff", amTariff, "noEvent");
  };

  const calcTariffAll = () =>
    gridRefs.current["grid1"].current?.forEachNode(calcTariff);

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDatas.freeform1,
        etcProps: {},
        events: {
          onValueChanged: onValueChanged,
          onContextMenu: onContextMenu,
        },
      },
    }),
    [formControls, formDatas]
  );

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onCellValueChanged: onCellValueChangedL,
          onBtnDeleteClick: onDeleteRow,
          onBeforeAddRow: onBeforeAddRow,
          onAfterSetFooter: onAfterSetFooter,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnForm
            ref={formRefs.current["freeform1"]}
            data={forms.freeform1.data}
            controls={forms.freeform1.controls}
            etcProps={forms.freeform1.etcProps}
            events={forms.freeform1.events}
          ></SnForm>
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "통관정보",
            }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"B/L적용"}
                  onClick={() => {
                    onGridBtnsClick("btnApplyBl");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_TRA_IBL_REG_002;
